import React from "react";

interface AnimatedStatusTextProps {
  baseText: string;
}

type RenderWithErrorFn = (content: React.ReactNode) => React.ReactNode;

interface IssueToolBlockProps {
  args: any;
  status: "in-progress" | "succeeded" | "failed" | "aborted";
  toolResult?: any;
  renderWithErrorIfFailed: RenderWithErrorFn;
  AnimatedStatusText: React.FC<AnimatedStatusTextProps>;
}

/**
 * Unwrap tool result from various wrapper formats (output.value, result, content, value)
 */
const unwrapToolResult = (toolResult: any): any => {
  if (!toolResult) return null;
  try {
    let parsed =
      typeof toolResult === "string" ? JSON.parse(toolResult) : toolResult;

    // Unwrap known wrapper formats recursively
    if (parsed?.output?.value !== undefined) {
      return unwrapToolResult(parsed.output.value);
    }
    if (parsed?.result && typeof parsed.result === "object" && !parsed.status) {
      return unwrapToolResult(parsed.result);
    }
    if (
      parsed?.content &&
      typeof parsed.content === "object" &&
      !parsed.status
    ) {
      return unwrapToolResult(parsed.content);
    }
    if (
      parsed?.value &&
      typeof parsed.value === "object" &&
      Object.keys(parsed).length === 1
    ) {
      return unwrapToolResult(parsed.value);
    }

    return parsed;
  } catch {
    return null;
  }
};

/**
 * CreateIssueTool - Shows title being created/created
 */
export const CreateIssueToolBlock: React.FC<IssueToolBlockProps> = ({
  args,
  status,
  renderWithErrorIfFailed,
  AnimatedStatusText,
}) => {
  const title = args?.title || "";

  if (status === "in-progress") {
    return (
      <div className="text-xs text-secondary-n-400">
        {title ? (
          <span>
            Creating: <span className="font-medium">{title}</span>
          </span>
        ) : (
          <AnimatedStatusText baseText="Creating issue" />
        )}
      </div>
    );
  }

  return renderWithErrorIfFailed(
    <div className="text-xs text-secondary-n-400">
      <span>
        Created: <span className="font-medium">{title}</span>
      </span>
    </div>,
  );
};

/**
 * UpdateIssueTool - Shows count and titles of updated issues
 */
export const UpdateIssueToolBlock: React.FC<IssueToolBlockProps> = ({
  args,
  status,
  toolResult,
  renderWithErrorIfFailed,
  AnimatedStatusText,
}) => {
  // Handle batch update format: { updates: [...] }
  const updates = args?.updates || [];
  const updateCount = updates.length;

  // Try to get result info
  let successCount: number | null = null;
  let updatedTitles: string[] = [];
  if (toolResult && status === "succeeded") {
    const result = unwrapToolResult(toolResult);
    successCount = result?.succeeded ?? null;
    // Extract titles from successful results
    if (result?.results) {
      updatedTitles = result.results
        .filter((r: any) => r.success && r.title)
        .map((r: any) => r.title)
        .slice(0, 3); // Show max 3 titles
    }
  }

  if (status === "in-progress") {
    return (
      <div className="text-xs text-secondary-n-400">
        {updateCount > 1 ? (
          <AnimatedStatusText baseText={`Updating ${updateCount} issues`} />
        ) : (
          <AnimatedStatusText baseText="Updating issue" />
        )}
      </div>
    );
  }

  return renderWithErrorIfFailed(
    <div className="text-xs text-secondary-n-400">
      {successCount !== null ? (
        <span>
          Updated{" "}
          <span className="font-medium">
            {successCount} {successCount === 1 ? "issue" : "issues"}
          </span>
          {updatedTitles.length > 0 && (
            <>
              :{" "}
              <span className="font-medium">
                {updatedTitles.join(", ")}
                {successCount > updatedTitles.length && "..."}
              </span>
            </>
          )}
        </span>
      ) : (
        <span>Issues updated</span>
      )}
    </div>,
  );
};

/**
 * DeleteIssueTool - Simple delete status
 */
export const DeleteIssueToolBlock: React.FC<IssueToolBlockProps> = ({
  status,
  renderWithErrorIfFailed,
  AnimatedStatusText,
}) => {
  if (status === "in-progress") {
    return <AnimatedStatusText baseText="Deleting issue" />;
  }

  return renderWithErrorIfFailed(
    <span className="text-xs text-secondary-n-400">Issue deleted</span>,
  );
};

/**
 * ListIssuesTool - Shows filters and count of issues found
 */
export const ListIssuesToolBlock: React.FC<IssueToolBlockProps> = ({
  args,
  status,
  toolResult,
  renderWithErrorIfFailed,
  AnimatedStatusText,
}) => {
  const filterStatus = args?.status;
  const filterPriority = args?.priority;

  // Try to get count from result
  let issueCount: number | null = null;
  if (toolResult && status === "succeeded") {
    const result = unwrapToolResult(toolResult);
    issueCount = result?.count ?? result?.issues?.length ?? null;
  }

  if (status === "in-progress") {
    return (
      <div className="text-xs text-secondary-n-400">
        {filterStatus || filterPriority ? (
          <span>
            Listing issues
            {filterStatus && <span> &bull; Status: {filterStatus}</span>}
            {filterPriority && <span> &bull; Priority: {filterPriority}</span>}
          </span>
        ) : (
          <AnimatedStatusText baseText="Listing issues" />
        )}
      </div>
    );
  }

  return renderWithErrorIfFailed(
    <div className="text-xs text-secondary-n-400">
      {issueCount !== null ? (
        <span>
          Found <span className="font-medium">{issueCount}</span>{" "}
          {issueCount === 1 ? "issue" : "issues"}
        </span>
      ) : (
        <span>Issues listed</span>
      )}
    </div>,
  );
};

/**
 * ReadIssueTool - Shows issue title from result
 */
export const ReadIssueToolBlock: React.FC<IssueToolBlockProps> = ({
  args,
  status,
  toolResult,
  renderWithErrorIfFailed,
  AnimatedStatusText,
}) => {
  const canvasId = args?.canvas_id || "";

  // Try to get title from result
  let issueTitle: string | null = null;
  if (toolResult && status === "succeeded") {
    const result = unwrapToolResult(toolResult);
    issueTitle = result?.issue?.title ?? null;
  }

  if (status === "in-progress") {
    return <AnimatedStatusText baseText="Reading issue" />;
  }

  return renderWithErrorIfFailed(
    <div className="text-xs text-secondary-n-400">
      {issueTitle ? (
        <span>
          Read: <span className="font-medium">{issueTitle}</span>
        </span>
      ) : (
        <span className="font-mono">{canvasId.slice(0, 8)}...</span>
      )}
    </div>,
  );
};

/**
 * ExploreCodebaseTool - Shows query and thoroughness level
 */
export const ExploreCodebaseToolBlock: React.FC<IssueToolBlockProps> = ({
  args,
  status,
  renderWithErrorIfFailed,
  AnimatedStatusText,
}) => {
  const query = args?.query || "";
  const thoroughness = args?.thoroughness || "medium";

  // Truncate query if too long
  const truncatedQuery = query.length > 80 ? query.slice(0, 77) + "..." : query;

  if (status === "in-progress") {
    return (
      <div className="text-xs text-secondary-n-400 space-y-1">
        <div>
          <AnimatedStatusText baseText="Exploring codebase" />
        </div>
        {query && <div className="text-secondary-n-500">{truncatedQuery}</div>}
        <div className="text-secondary-n-600 capitalize">
          Depth: {thoroughness}
        </div>
      </div>
    );
  }

  return renderWithErrorIfFailed(
    <div className="text-xs text-secondary-n-400 space-y-1">
      {query && <div className="text-secondary-n-500">{truncatedQuery}</div>}
      <div className="text-secondary-n-600 capitalize">
        Depth: {thoroughness}
      </div>
    </div>,
  );
};

/**
 * Check if toolName is an issue/PM agent tool
 */
export const isIssueOrPMAgentTool = (toolName: string): boolean => {
  return [
    "createIssueTool",
    "updateIssueTool",
    "deleteIssueTool",
    "listIssuesTool",
    "readIssueTool",
    "exploreCodebaseTool",
  ].includes(toolName);
};

/**
 * Get the appropriate block component for an issue/PM agent tool
 */
export const getIssueToolBlock = (
  toolName: string,
): React.FC<IssueToolBlockProps> | null => {
  switch (toolName) {
    case "createIssueTool":
      return CreateIssueToolBlock;
    case "updateIssueTool":
      return UpdateIssueToolBlock;
    case "deleteIssueTool":
      return DeleteIssueToolBlock;
    case "listIssuesTool":
      return ListIssuesToolBlock;
    case "readIssueTool":
      return ReadIssueToolBlock;
    case "exploreCodebaseTool":
      return ExploreCodebaseToolBlock;
    default:
      return null;
  }
};
